import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, Download, FileSpreadsheet, Printer, Users, Mail } from "lucide-react";
import { toast } from "sonner";
import { useBranding } from "@/context/BrandingContext";

export default function Settings() {
  const [charges, setCharges] = useState([]);
  const [procs, setProcs] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [ptImportResult, setPtImportResult] = useState(null);
  const [ptImporting, setPtImporting] = useState(false);
  const [savingPrint, setSavingPrint] = useState(false);
  const brand = useBranding();
  const [printPrefs, setPrintPrefs] = useState({
    show_doctor_sig_rx: false,
    show_patient_sig_bill: true,
  });
  const fileRef = useRef(null);
  const ptFileRef = useRef(null);

  // Daily Collection Report email settings
  const [daily, setDaily] = useState({ enabled: false, recipients: [] });
  const [dailyText, setDailyText] = useState("");   // comma-separated raw input
  const [dailySaving, setDailySaving] = useState(false);
  const [dailyTesting, setDailyTesting] = useState(false);

  useEffect(() => {
    api.get("/emergency-charges").then((r) => setCharges(r.data));
    api.get("/procedure-charges").then((r) => setProcs(r.data));
    api.get("/settings/daily-email").then((r) => {
      setDaily({ enabled: !!r.data.enabled, recipients: r.data.recipients || [] });
      setDailyText((r.data.recipients || []).join(", "));
    }).catch(() => {});
  }, []);

  const saveDaily = async () => {
    const recipients = dailyText.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
    if (recipients.length === 0) return toast.error("Add at least one email");
    setDailySaving(true);
    try {
      const { data } = await api.put("/settings/daily-email", { enabled: daily.enabled, recipients });
      setDaily({ enabled: data.enabled, recipients: data.recipients });
      setDailyText(data.recipients.join(", "));
      toast.success(`Daily report ${data.enabled ? "enabled" : "disabled"} · ${data.recipients.length} recipient(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setDailySaving(false); }
  };
  const sendTestNow = async () => {
    setDailyTesting(true);
    try {
      const { data } = await api.post("/reports/daily-email/test");
      const results = data.results || [];
      const okCount = results.filter((r) => r.ok).length;
      toast.success(`Sent to ${okCount}/${results.length} recipient(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    } finally { setDailyTesting(false); }
  };

  useEffect(() => {
    // Seed toggles from the currently-loaded branding
    if (!brand.loading) {
      setPrintPrefs({
        show_doctor_sig_rx: !!brand.show_doctor_sig_rx,
        show_patient_sig_bill: brand.show_patient_sig_bill !== false,
      });
    }
  }, [brand.loading, brand.show_doctor_sig_rx, brand.show_patient_sig_bill]);

  const savePrintPrefs = async () => {
    setSavingPrint(true);
    try {
      const payload = {
        name: brand.name,
        short_name: brand.short_name || brand.name,
        tagline: brand.tagline || "",
        address: brand.address || "",
        phones: brand.phones || [],
        email: brand.email || "",
        website: brand.website || "",
        abdm_enabled: !!brand.abdm_enabled,
        show_doctor_sig_rx: printPrefs.show_doctor_sig_rx,
        show_patient_sig_bill: printPrefs.show_patient_sig_bill,
      };
      await api.put("/branding", payload);
      await brand.reload();
      toast.success("Print preferences saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSavingPrint(false);
    }
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/medicines/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`Imported: ${data.inserted} new · ${data.updated} updated`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const doPatientImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm(`Import patients from "${file.name}"?\nDuplicates (same name + mobile) are skipped automatically.`)) {
      if (ptFileRef.current) ptFileRef.current.value = "";
      return;
    }
    setPtImporting(true);
    setPtImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/patients/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPtImportResult(data);
      toast.success(`Imported: ${data.inserted} new · ${data.skipped} skipped`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Patient import failed");
    } finally {
      setPtImporting(false);
      if (ptFileRef.current) ptFileRef.current.value = "";
    }
  };

  const downloadTemplate = () => {
    const rows = [
      "name,generic,manufacturer,batch,expiry,mrp,stock,category,reorder_level",
      "PARACETAMOL 500,Paracetamol,Cipla,B1024,2027-06-30,15,500,Tablet,50",
      "AMOXICILLIN 500,Amoxicillin,Sun Pharma,AMX22,2026-11-30,42,240,Capsule,30",
    ].join("\n");
    const blob = new Blob([rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "medicine-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <h1 className="font-display text-2xl font-semibold">Settings</h1>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border p-4">
          <div className="label-tiny mb-3">Hospital Profile</div>
          <div className="text-sm space-y-1">
            <div><b>Name:</b> Divine Hospital</div>
            <div><b>Type:</b> Multispeciality</div>
            <div><b>Location:</b> Jamnagar, Gujarat</div>
            <div><b>Capacity:</b> 50 beds</div>
            <div><b>Facilities:</b> ICU · OT · Dialysis · Pharmacy · Lab · Emergency · IPD · OPD</div>
          </div>
        </div>

        <div className="bg-white border p-4">
          <div className="label-tiny mb-3 flex items-center gap-2"><Printer className="w-3.5 h-3.5"/> Print Preferences</div>
          <p className="text-xs text-slate-500 mb-3">
            Toggle these off when your doctors stamp physical Rx / discharge and you don't need a printed signature line.
          </p>
          <label className="flex items-start gap-3 mb-3 cursor-pointer text-sm">
            <input
              type="checkbox"
              className="mt-0.5 accent-blue-700"
              checked={printPrefs.show_doctor_sig_rx}
              onChange={(e) => setPrintPrefs((p) => ({ ...p, show_doctor_sig_rx: e.target.checked }))}
              data-testid="print-toggle-doctor-sig"
            />
            <span>
              <div className="font-medium">Show doctor signature on Rx / Discharge</div>
              <div className="text-[11px] text-slate-500">Prints the uploaded digital signature above the doctor's name. Keep OFF if your doctors sign with a rubber stamp.</div>
            </span>
          </label>
          <label className="flex items-start gap-3 mb-3 cursor-pointer text-sm">
            <input
              type="checkbox"
              className="mt-0.5 accent-blue-700"
              checked={printPrefs.show_patient_sig_bill}
              onChange={(e) => setPrintPrefs((p) => ({ ...p, show_patient_sig_bill: e.target.checked }))}
              data-testid="print-toggle-patient-sig"
            />
            <span>
              <div className="font-medium">Show "Patient / Attendant Signature" on OPD bill</div>
              <div className="text-[11px] text-slate-500">IPD final bills never carry this line by hospital policy.</div>
            </span>
          </label>
          <Button onClick={savePrintPrefs} disabled={savingPrint} className="bg-blue-700 hover:bg-blue-800 rounded-sm" data-testid="print-prefs-save">
            {savingPrint ? "Saving…" : "Save Print Preferences"}
          </Button>
        </div>

        <div className="bg-white border p-4" data-testid="daily-email-card">
          <div className="label-tiny mb-3 flex items-center gap-2"><Mail className="w-3.5 h-3.5"/> Daily Collection Report · Auto-Email</div>
          <p className="text-xs text-slate-500 mb-3">
            Emails a summary of the day's <b>Collection &amp; Activity Report</b> (OPD count, IPD count,
            payment-mode split, top-8 doctor revenue) to the recipients below every night at
            <b> 11:30 PM IST</b>. Toggle off any night you don't want it sent.
          </p>
          <label className="flex items-start gap-3 mb-3 cursor-pointer text-sm">
            <input
              type="checkbox"
              className="mt-0.5 accent-blue-700"
              checked={daily.enabled}
              onChange={(e) => setDaily((p) => ({ ...p, enabled: e.target.checked }))}
              data-testid="daily-email-enabled"
            />
            <span>
              <div className="font-medium">Enable nightly auto-email</div>
              <div className="text-[11px] text-slate-500">Runs at 23:30 IST · one email per recipient · unsubscribe by unchecking this box.</div>
            </span>
          </label>
          <label className="text-xs text-slate-500 block mb-1">Recipient emails (comma separated)</label>
          <textarea
            rows={2}
            className="w-full border rounded-sm px-2 py-1.5 text-sm font-mono"
            value={dailyText}
            onChange={(e) => setDailyText(e.target.value)}
            placeholder="admin@hospital.com, owner@hospital.com"
            data-testid="daily-email-recipients"
          />
          <div className="text-[10px] text-slate-400 mt-1">Default: gujaratsanchar@gmail.com — replace or add as many as you need.</div>
          <div className="flex gap-2 mt-3">
            <Button onClick={saveDaily} disabled={dailySaving} className="bg-blue-700 hover:bg-blue-800 rounded-sm" data-testid="daily-email-save">
              {dailySaving ? "Saving…" : "Save"}
            </Button>
            <Button onClick={sendTestNow} disabled={dailyTesting} variant="outline" className="rounded-sm" data-testid="daily-email-test">
              {dailyTesting ? "Sending…" : "Send Test Now"}
            </Button>
          </div>
        </div>

        <div className="bg-white border p-4">
          <div className="label-tiny mb-3">Emergency Charges Master</div>
          {charges.map((c) => (
            <div key={c.id} className="flex justify-between border-b py-1 text-sm"><span>{c.name}</span><span className="font-mono">₹{c.amount}</span></div>
          ))}
        </div>

        <div className="bg-white border p-4">
          <div className="label-tiny mb-3">Procedure Charges Master</div>
          {procs.map((c) => (
            <div key={c.id} className="flex justify-between border-b py-1 text-sm"><span>{c.name}</span><span className="font-mono">₹{c.amount}</span></div>
          ))}
        </div>

        <div className="bg-white border p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="label-tiny flex items-center gap-2"><Users className="w-3.5 h-3.5"/> Patient Register · Bulk Import</div>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Upload an <b>.xls / .xlsx</b> patient register (Divine format: Sr No · Name+Address · Mobile · Age · Gender).
            Duplicates (same name + mobile) are skipped automatically. Each new patient gets a fresh UHID that continues after the current max.
          </p>
          <input
            ref={ptFileRef}
            type="file"
            accept=".xls,.xlsx"
            onChange={doPatientImport}
            disabled={ptImporting}
            className="hidden"
            data-testid="pt-import-input"
          />
          <Button
            onClick={() => ptFileRef.current?.click()}
            disabled={ptImporting}
            data-testid="pt-import-btn"
            className="bg-teal-700 hover:bg-teal-800 rounded-sm"
          >
            <Upload className="w-4 h-4 mr-1"/> {ptImporting ? "Importing patients…" : "Upload Patient Register (XLS/XLSX)"}
          </Button>

          {ptImportResult && (
            <div className="mt-3 border p-2 text-xs" data-testid="pt-import-result">
              <div className="flex items-center gap-2 text-teal-800 font-semibold mb-1"><FileSpreadsheet className="w-3 h-3"/> Patient import complete</div>
              <div>Parsed: {ptImportResult.parsed} · Inserted: <b>{ptImportResult.inserted}</b> · Skipped duplicates: {ptImportResult.skipped}</div>
              <div className="text-slate-500 mt-1">Next auto-UHID: <span className="font-mono">{ptImportResult.next_uhid}</span></div>
            </div>
          )}
        </div>

        <div className="bg-white border p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="label-tiny">Medicine Master · Bulk Import</div>
            <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="med-template-btn"><Download className="w-3 h-3 mr-1"/> Template</Button>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Upload a CSV or Excel (.xlsx / .xls). Required column: <b>name</b>. Optional: <b>generic, manufacturer, batch, expiry, mrp, stock, category, reorder_level</b>. Existing medicines are updated by name.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={doImport}
            disabled={importing}
            className="hidden"
            data-testid="med-file-input"
          />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={importing}
            data-testid="med-import-btn"
            className="bg-blue-700 hover:bg-blue-800 rounded-sm"
          >
            <Upload className="w-4 h-4 mr-1"/> {importing ? "Importing..." : "Upload CSV / Excel"}
          </Button>

          {importResult && (
            <div className="mt-3 border p-2 text-xs" data-testid="med-import-result">
              <div className="flex items-center gap-2 text-teal-800 font-semibold mb-1"><FileSpreadsheet className="w-3 h-3"/> Import complete</div>
              <div>Processed: {importResult.processed} · Inserted: {importResult.inserted} · Updated: {importResult.updated}</div>
              {importResult.errors && importResult.errors.length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-red-700">{importResult.errors.length} row(s) skipped</summary>
                  <ul className="ml-3 mt-1 list-disc text-red-600">
                    {importResult.errors.slice(0, 10).map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
